/* eslint-disable complexity */
import { formatDate } from '@strata/helper-functions';
import { ClampedContent } from '@strata/react-clamped-content';
import { InlineMessage } from '@strata/react-inline-message';
import { DetailLabel, DetailLabelValuePair } from '@strata/react-label-value-pairs';

import { PriorAuthStatus } from './PriorAuthStatus';

import {
    MilestoneToStatusMessageMap,
    MilestoneType,
    PriorAuthErrorDisplayTextMap,
} from 'services/constants';
import { type EPaResponse } from 'store/api/epaApi';
import { type IntakeCase } from 'store/api/intakeCaseApi';

interface EpaCaseDetailFieldsProps {
    activeResponse?: EPaResponse;
    intakeCaseDetails?: IntakeCase;
    milestoneType?: (typeof MilestoneType)[keyof typeof MilestoneType];
    showErrorResponse?: boolean;
}

const getStatusMessage = (milestoneType?: (typeof MilestoneType)[keyof typeof MilestoneType]) =>
    MilestoneToStatusMessageMap[milestoneType as keyof typeof MilestoneToStatusMessageMap];

export const EpaCaseDetailFields = ({
    activeResponse = undefined,
    intakeCaseDetails = undefined,
    milestoneType = undefined,
    showErrorResponse = false,
}: EpaCaseDetailFieldsProps) => {
    const reasonCode = activeResponse?.reasonCode;

    const showStatusMessage =
        milestoneType &&
        milestoneType !== MilestoneType.QUESTIONS_AVAILABLE &&
        milestoneType !== MilestoneType.QUESTIONS_SUBMITTED;

    const statusMessage = getStatusMessage(milestoneType);
    const { intent, label } = statusMessage ?? { intent: undefined, label: undefined };

    const errorResponse = showErrorResponse ? activeResponse?.errorResponse : undefined;

    return (
        <div
            className="flex__gap--medium margin__bottom--medium"
            style={{ flexDirection: 'column' }}
        >
            {errorResponse && (
                <>
                    <InlineMessage
                        wrapText
                        intent={intent}
                        label="PA failed. Please check intake case for inaccurate or missing information."
                    />
                    <InlineMessage
                        wrapText
                        intent={InlineMessage.Intent.ERROR}
                        label={errorResponse}
                    />
                </>
            )}
            {!errorResponse && showStatusMessage && (
                <InlineMessage
                    wrapText
                    intent={intent}
                    label={reasonCode ? PriorAuthErrorDisplayTextMap[reasonCode] : label}
                />
            )}
            <div className="sds_intake-case-details__epa-details__fields">
                {activeResponse?.externalCaseId && (
                    <DetailLabelValuePair label="Case ID" value={activeResponse.externalCaseId} />
                )}
                {activeResponse?.determination && (
                    <div>
                        <DetailLabel label="Prior Auth Status" />
                        <PriorAuthStatus
                            response={activeResponse}
                            className="sds_intake-case-details__epa-details__determination"
                        />
                    </div>
                )}
                {activeResponse?.dueDate && (
                    <DetailLabelValuePair
                        label="Due Date"
                        value={formatDate(activeResponse.dueDate)}
                    />
                )}

                {activeResponse?.priorAuthNumber && (
                    <DetailLabelValuePair
                        label="Prior Auth Number"
                        value={activeResponse.priorAuthNumber}
                    />
                )}
                {activeResponse?.pharmacyType && (
                    <DetailLabelValuePair
                        label="Pharmacy Type"
                        value={activeResponse.pharmacyType}
                    />
                )}
                {intakeCaseDetails?.quantity != null && (
                    <DetailLabelValuePair label="Quantity" value={intakeCaseDetails.quantity} />
                )}
                {intakeCaseDetails?.daysSupply != null && (
                    <DetailLabelValuePair
                        label="Days Supply"
                        value={intakeCaseDetails.daysSupply}
                    />
                )}
                {activeResponse?.numberOfCycles != null && (
                    <DetailLabelValuePair
                        label="Number Of Cycles"
                        value={activeResponse.numberOfCycles}
                    />
                )}
                {intakeCaseDetails?.refills != null && (
                    <DetailLabelValuePair
                        label="Number Of Refills"
                        value={intakeCaseDetails.refills}
                    />
                )}
                {activeResponse?.effectiveDate && activeResponse?.expirationDate && (
                    <DetailLabelValuePair
                        label="Authorization Period"
                        value={`${formatDate(activeResponse.effectiveDate)} - ${formatDate(activeResponse.expirationDate)}`}
                    />
                )}
            </div>
            {activeResponse?.paNote && (
                <DetailLabelValuePair
                    label="Note"
                    value={<ClampedContent message={activeResponse.paNote} />}
                />
            )}
        </div>
    );
};
